import React, { useCallback, useEffect, useMemo, useState } from "react";

import { StatsSummary as StatsSummaryComponent } from "heat-next";
import { isArray } from "lodash";

import { useModal } from "@/context/modal/modal";
import { useSessionDetailsContext } from "@/context/session-details/session-details";

import { ComponentWrapper } from "@/pages/shared/shared.styles";

import ContentLoader from "@/components/content-loader/content-loader";
import EmptyState from "@/components/empty-state/empty-state";
import { EmptyStateType } from "@/components/empty-state/empty-state.types";
import TargetModalContent from "@/components/ui/modal-with-context/modal-contents/session-details-page/target-modal-content/target-modal-content";
import {
    MODAL_HEIGHT_PERCENTAGE,
    MODAL_NON_SCROLLABLE_CONTENT_HEIGHT_DEFAULT,
} from "@/components/ui/modal-with-context/modal-contents/utils/modal-content-variables";

import base from "@/themes/base/base";

import { useSessionInfo } from "../../hooks/useSessionInfo";

interface StatsSummaryWithModalReportProps {
    subDimensionsData?: any; //for when StatsSummaryWithModalReport is rendered inside the modal
    isInsideModal?: boolean;
    componentDescription?: string;
    onButtonClick?: () => void;
}

const StatsSummaryWithModalReport: React.FC<StatsSummaryWithModalReportProps> = ({
    subDimensionsData,
    isInsideModal,
}) => {
    const { openModal, setWidth, setHeight } = useModal();

    const { getSessionSubtitleDetails, logoUrl } = useSessionInfo();

    const {
        combinedCustomTopStatsData,
        isFetchingCombinedCustomTopStats,
        isErrorCombinedCustomTopStats,
        targetFilterOptions,
        setTargetFilterOptions,
        setSelectedTarget,
        setSelectedTargetIndex,
        subDimensionsData: subDimensionsLineProcessData,
        isFetchingSubDimensions: isFetchingSubDimensionsLineProcess,
        isErrorSubDimensions: isErrorSubDimensionsLineProcess,
    } = useSessionDetailsContext();

    const hasSubDimensionsLineProcessData =
        isArray(subDimensionsLineProcessData) && subDimensionsLineProcessData?.length > 0;

    const [scrollableContainerHeight, setScrollableContainerHeight] = useState(
        () =>
            window.innerHeight * MODAL_HEIGHT_PERCENTAGE -
            MODAL_NON_SCROLLABLE_CONTENT_HEIGHT_DEFAULT,
    );

    //to update Modals' ComponentsScrollableWrapper height, when screen height changes
    useEffect(() => {
        const handleScrollableContainerResize = () => {
            const updatedHeight =
                window.innerHeight * MODAL_HEIGHT_PERCENTAGE -
                MODAL_NON_SCROLLABLE_CONTENT_HEIGHT_DEFAULT;
            setScrollableContainerHeight(updatedHeight);
        };

        window.addEventListener("resize", handleScrollableContainerResize);

        return () => window.removeEventListener("resize", handleScrollableContainerResize);
    }, []);

    //to update the DOM element's height, since styled components are not receiving the updated height
    useEffect(() => {
        const componentsScrollableWrapper = document.getElementById(
            "components-scrollable-wrapper",
        );

        if (componentsScrollableWrapper) {
            componentsScrollableWrapper.style.height = `${scrollableContainerHeight}px`;
        }
    }, [scrollableContainerHeight]);

    const areAllValuesEmpty = combinedCustomTopStatsData?.every((value: any) => {
        return value?.length === 0;
    });

    const hasEmptyState =
        !combinedCustomTopStatsData ||
        combinedCustomTopStatsData?.length === 0 ||
        areAllValuesEmpty;

    const isSubDimensionsDataEmpty =
        !subDimensionsData ||
        subDimensionsData?.length === 0 ||
        subDimensionsData?.value?.length === 0;

    const filterOptions = useMemo(() => {
        const lineProcessComponentData = (
            hasSubDimensionsLineProcessData ? subDimensionsLineProcessData : []
        )?.map((lineProcessItem: any) => {
            return lineProcessItem?.id;
        });

        return lineProcessComponentData;
    }, [subDimensionsLineProcessData]);

    useEffect(() => {
        setTargetFilterOptions(filterOptions);
    }, [subDimensionsLineProcessData]);

    const handleModalOpen = useCallback(() => {
        setWidth("77.25682295%"); //for 1104px modal width, in breakpoint 1440px
        setHeight("87.4%");

        setSelectedTarget(targetFilterOptions && targetFilterOptions[0]);
        setSelectedTargetIndex(0);

        openModal({
            content: (
                <TargetModalContent
                    scrollableContainerHeight={scrollableContainerHeight}
                    logoUrl={logoUrl}
                    getSessionSubtitleDetails={getSessionSubtitleDetails}
                    subDimensionsData={subDimensionsLineProcessData}
                />
            ),
            backgroundColor: `${base.colors.neutral.grey[900]}`,
            borderRadius: "0.25rem",
            padding: "1.5rem 1.5rem 0 1.5rem",
            boxShadow: "0px 0px 4px 1px rgba(255, 255, 255, 0.25)",
        });
    }, [
        targetFilterOptions,
        scrollableContainerHeight,
        logoUrl,
        getSessionSubtitleDetails,
        setSelectedTargetIndex,
        subDimensionsLineProcessData,
    ]);

    if (isFetchingCombinedCustomTopStats) {
        return <ContentLoader height="10.125rem" />;
    }

    const isEmptyState = isInsideModal ? isSubDimensionsDataEmpty : hasEmptyState;

    const data = isInsideModal ? subDimensionsData : combinedCustomTopStatsData;

    const buttonText = data?.[0]?.buttonText || "View Target Report";

    if (isEmptyState) {
        return (
            <ComponentWrapper>
                <EmptyState
                    title="session-details:stats-summary:empty-state:title"
                    emptyStateType={EmptyStateType.Charts}
                />
            </ComponentWrapper>
        );
    }

    if (
        (!subDimensionsData &&
            isErrorCombinedCustomTopStats &&
            !isFetchingCombinedCustomTopStats) ||
        isErrorSubDimensionsLineProcess
    ) {
        return (
            <ComponentWrapper>
                <EmptyState
                    title="error-state:title"
                    description="error-state:description"
                    emptyStateType={EmptyStateType.Error}
                />
            </ComponentWrapper>
        );
    }

    return (
        <StatsSummaryComponent
            data={data}
            onButtonClick={handleModalOpen}
            buttonText={buttonText}
            disableButton={isFetchingCombinedCustomTopStats || isFetchingSubDimensionsLineProcess}
        />
    );
};

export default StatsSummaryWithModalReport;
